import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useSecureChat } from "./SecureChatContext";
import { getUserByName } from "../utilities/ApiCalls";
import ProfileImage from "../../Componets/ProfileImage";
const api = require("../../api/secureMessageApi");
const Constant = require("../Constant");

const MIN_QUERY_LENGTH = 2;
const STEP = {
  SELECT: "select",
  DETAILS: "details",
};

const NewGroupModal = ({ onClose, onGroupCreated }) => {
  const { currentUser } = useSecureChat();
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [step, setStep] = useState(STEP.SELECT);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const proxiedUrl = useCallback((url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com"))
      return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  }, []);

  const searchUsers = useCallback(
    async (query) => {
      if (!query.trim() || query.trim().length < MIN_QUERY_LENGTH) {
        setSearchResults([]);
        return;
      }

      try {
        setIsSearching(true);
        setError(null);

        const resp = await getUserByName(encodeURIComponent(query));
        const users = resp?.data?.data || resp?.data || [];

        const filteredUsers = users.filter((user) => {
          if (user._id === currentUser?._id) {
            return false;
          }
          return !selectedUsers.some((selected) => selected._id === user._id);
        });
        setSearchResults(filteredUsers);
      } catch (err) {
        console.error("Error searching users:", err);
        setError("Failed to search users");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [currentUser, selectedUsers]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchUsers]);

  const handleToggleUser = useCallback((user) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((item) => item._id === user._id);
      const next = exists
        ? prev.filter((item) => item._id !== user._id)
        : [...prev, user];
      return next;
    });
    setError(null);
  }, []);

  const handleRemoveUser = useCallback((event, userId) => {
    event.stopPropagation();
    setSelectedUsers((prev) => prev.filter((user) => user._id !== userId));
    setError(null);
  }, []);

  useEffect(() => {
    if (step === STEP.DETAILS && selectedUsers.length === 0) {
      setStep(STEP.SELECT);
    }
  }, [step, selectedUsers.length]);

  const handleNext = useCallback(() => {
    if (selectedUsers.length === 0) {
      setError("Select at least one participant");
      return;
    }
    setError(null);
    setStep(STEP.DETAILS);
  }, [selectedUsers.length]);

  const handleBack = useCallback(() => {
    setStep(STEP.SELECT);
    setError(null);
  }, []);

  const createGroup = useCallback(async () => {
    const trimmedName = groupName.trim();
    const trimmedDescription = groupDescription.trim();

    if (!trimmedName) {
      setError("Group name is required");
      return;
    }

    if (selectedUsers.length === 0) {
      setError("Select at least one participant");
      setStep(STEP.SELECT);
      return;
    }

    try {
      setIsCreating(true);
      setError(null);

      const userIds = selectedUsers.map((user) => user._id);
      const response = await api.createGroup(
        userIds,
        trimmedName,
        trimmedDescription
      );
      const groupData = response?.data || response;

      if (groupData?._id) {
        const otherUsers = Array.isArray(groupData?.users)
          ? groupData.users.filter((user) => user._id !== currentUser?._id)
          : selectedUsers;

        const populatedGroup = {
          ...groupData,
          otherUsers,
          displayName: groupData.chatName || trimmedName,
          displayPic: groupData.groupImage || "",
          unreadCount:
            typeof groupData.unreadCount === "number"
              ? groupData.unreadCount
              : Number(groupData.unreadCount) || 0,
        };

        onGroupCreated(populatedGroup);
        onClose();
      } else {
        throw new Error("No group ID returned");
      }
    } catch (err) {
      console.error("Error creating group:", err);
      setError(err?.message || "Failed to create group");
    } finally {
      setIsCreating(false);
    }
  }, [
    groupName,
    groupDescription,
    selectedUsers,
    currentUser,
    onGroupCreated,
    onClose,
  ]);

  const handleOverlayClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const renderInitial = useCallback((name) => {
    return name?.charAt(0).toUpperCase() || "U";
  }, []);

  return createPortal(
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <HeaderText>
            <Title>Create Group</Title>
            <SubTitle>
              Step {step === STEP.SELECT ? "1" : "2"} of 2 ·{" "}
              {step === STEP.SELECT ? "Add participants" : "Group details"}
            </SubTitle>
          </HeaderText>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalContent>
          {step === STEP.SELECT && (
            <>
              <Section>
                <SectionLabel>Add participants</SectionLabel>
                <SearchInput
                  type="text"
                  placeholder="Search users by name..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  autoFocus
                />
                <HelperText>
                  Start typing to search. Select at least one participant to
                  continue.
                </HelperText>
              </Section>

              {error && (
                <ErrorContainer>
                  <ErrorText>{error}</ErrorText>
                </ErrorContainer>
              )}

              {isSearching && (
                <LoadingContainer>
                  <LoadingText>Searching...</LoadingText>
                </LoadingContainer>
              )}

              {!isSearching &&
                searchQuery.trim().length >= MIN_QUERY_LENGTH &&
                searchResults.length === 0 && (
                  <EmptyContainer>
                    <EmptyText>No users found</EmptyText>
                  </EmptyContainer>
                )}

              {searchResults.length > 0 && (
                <ResultsContainer>
                  {searchResults.map((user) => {
                    const isSelected = selectedUsers.some(
                      (item) => item._id === user._id
                    );
                    return (
                      <UserItem
                        key={user._id}
                        $isSelected={isSelected}
                        onClick={() => handleToggleUser(user)}
                      >
                        <UserAvatar user={user} proxiedUrl={proxiedUrl} />
                        <UserName>{user.name}</UserName>
                        <UserAction>
                          {isSelected ? "Selected" : "Add"}
                        </UserAction>
                      </UserItem>
                    );
                  })}
                </ResultsContainer>
              )}

              {selectedUsers.length > 0 && (
                <SelectedSection>
                  <SelectedHeader>
                    {selectedUsers.length}{" "}
                    {selectedUsers.length === 1
                      ? "participant"
                      : "participants"}{" "}
                    selected
                  </SelectedHeader>
                  <SelectedUsersContainer>
                    {selectedUsers.map((user) => (
                      <UserChip key={user._id}>
                        <ChipAvatar user={user} proxiedUrl={proxiedUrl} />
                        <ChipName>{user.name}</ChipName>
                        <ChipRemove
                          onClick={(event) => handleRemoveUser(event, user._id)}
                        >
                          ×
                        </ChipRemove>
                      </UserChip>
                    ))}
                  </SelectedUsersContainer>
                </SelectedSection>
              )}
            </>
          )}

          {step === STEP.DETAILS && (
            <>
              <Section>
                <SectionLabel>Group name</SectionLabel>
                <TextInput
                  type="text"
                  placeholder="Enter group name"
                  value={groupName}
                  onChange={(event) => {
                    setGroupName(event.target.value);
                    if (error) {
                      setError(null);
                    }
                  }}
                  autoFocus
                />
              </Section>

              <Section>
                <SectionLabel>Group description (optional)</SectionLabel>
                <TextArea
                  placeholder="Add a short description"
                  value={groupDescription}
                  onChange={(event) => setGroupDescription(event.target.value)}
                  rows={3}
                  maxLength={250}
                />
              </Section>

              <SelectedSection>
                <SelectedHeader>
                  Participants ({selectedUsers.length})
                </SelectedHeader>
                <SelectedUsersContainer>
                  {selectedUsers.map((user) => (
                    <UserChip key={user._id}>
                      <ChipAvatar user={user} proxiedUrl={proxiedUrl} />
                      <ChipName>{user.name}</ChipName>
                      <ChipRemove
                        onClick={(event) => handleRemoveUser(event, user._id)}
                      >
                        ×
                      </ChipRemove>
                    </UserChip>
                  ))}
                </SelectedUsersContainer>
                <EditParticipantsButton onClick={handleBack}>
                  Edit participants
                </EditParticipantsButton>
              </SelectedSection>

              {error && (
                <ErrorContainer>
                  <ErrorText>{error}</ErrorText>
                </ErrorContainer>
              )}
            </>
          )}
        </ModalContent>

        <ModalFooter>
          {step === STEP.SELECT ? (
            <>
              <FooterText>
                {selectedUsers.length === 0
                  ? "Select at least one participant"
                  : `${selectedUsers.length} ${
                      selectedUsers.length === 1
                        ? "participant"
                        : "participants"
                    } selected`}
              </FooterText>
              <NextButton
                onClick={handleNext}
                disabled={selectedUsers.length === 0}
              >
                Next
              </NextButton>
            </>
          ) : (
            <>
              <BackButton onClick={handleBack}>Back</BackButton>
              <PrimaryButton
                onClick={createGroup}
                disabled={
                  !groupName.trim() || selectedUsers.length === 0 || isCreating
                }
              >
                {isCreating ? "Creating..." : "Create Group"}
              </PrimaryButton>
            </>
          )}
        </ModalFooter>
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

// Sub-component to handle avatar with error fallback
const UserAvatar = ({ user, proxiedUrl }) => {
  return (
    <AvatarContainer>
      <ProfileImage
        src={user.profilePic}
        name={user.name}
        size={40}
        alt={user.name}
        proxiedUrl={proxiedUrl}
        avatarProps={{
          border: "2px solid var(--color-surface)",
        }}
        imgStyle={{
          border: "2px solid var(--color-surface)",
        }}
      />
    </AvatarContainer>
  );
};

// Sub-component to handle chip avatar with error fallback
const ChipAvatar = ({ user, proxiedUrl }) => {
  return (
    <ProfileImage
      src={user.profilePic}
      name={user.name}
      size={24}
      alt={user.name}
      proxiedUrl={proxiedUrl}
      avatarProps={{
        border: "1px solid var(--color-border)",
        fontSize: "12px",
      }}
      imgStyle={{
        border: "1px solid var(--color-border)",
      }}
    />
  );
};

export default NewGroupModal;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background-color: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--color-border);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
  font-family: "Merriweather", serif;
`;

const SubTitle = styled.span`
  font-size: 13px;
  color: var(--color-text-secondary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-background);
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 16px;
  outline: none;
  background-color: var(--color-background);
  color: var(--color-text);
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  resize: vertical;
  background-color: var(--color-background);
  color: var(--color-text);
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 16px;
  outline: none;
  background-color: var(--color-background);
  color: var(--color-text);
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const HelperText = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
`;

const ErrorContainer = styled.div`
  padding: 12px;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid var(--color-error);
  border-radius: 4px;
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--color-error);
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoadingText = styled.p`
  margin: 0;
  color: var(--color-text);
  font-size: 16px;
`;

const EmptyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
`;

const EmptyText = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 16px;
`;

const ResultsContainer = styled.div`
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background-color: var(--color-surface);
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$isSelected ? "var(--color-background)" : "transparent"};
  border-bottom: 1px solid var(--color-border);

  &:hover {
    background-color: var(--color-background);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const AvatarContainer = styled.div`
  width: 40px;
  height: 40px;
  margin-right: 12px;
  flex-shrink: 0;
`;

// Avatar and DefaultAvatar are now handled by ProfileImage component

const UserName = styled.span`
  flex: 1;
  font-size: 16px;
  color: var(--color-text);
  font-weight: 500;
  font-family: "Merriweather", serif;
`;

const UserAction = styled.span`
  font-size: 14px;
  color: var(--color-primary);
  font-weight: 500;
`;

const SelectedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: var(--color-background);
  border-radius: 6px;
  border: 1px solid var(--color-border);
`;

const SelectedHeader = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--color-text);
  font-weight: 500;
`;

const SelectedUsersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const UserChip = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  font-size: 13px;
  color: var(--color-text);
  gap: 6px;
`;

// ChipAvatarImage and ChipAvatarInitial are now handled by ProfileImage component

const ChipName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text);
`;

const ChipRemove = styled.button`
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;
  display: flex;
  align-items: center;

  &:hover {
    color: var(--color-error);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  background-color: var(--color-surface);
`;

const FooterText = styled.span`
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const NextButton = styled.button`
  background-color: var(--color-text);
  color: var(--color-surface);
  border: 1px solid var(--color-text);
  padding: 10px 22px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  &:disabled {
    background-color: var(--color-border);
    border-color: var(--color-border);
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  background-color: var(--color-text);
  color: var(--color-surface);
  border: 1px solid var(--color-text);
  padding: 10px 22px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  &:disabled {
    background-color: var(--color-border);
    border-color: var(--color-border);
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-background);
  }
`;

const EditParticipantsButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`;
